export type MinimizedViewField =
  | "title"
  | "tags"
  | "value"
  | "closeDate"
  | "contacts"
  | "assignee"
  | "inactivity";

export type MinimizedViewConfig = Record<MinimizedViewField, boolean>;

export const DEFAULT_MINIMIZED_VIEW: MinimizedViewConfig = {
  title: true,
  tags: false,
  value: false,
  closeDate: false,
  contacts: false,
  assignee: false,
  inactivity: false,
};

const columnStorageKey = (boardId: string, listId: string) =>
  `wembii.crm.minimizedView.${boardId}.${listId}`;

const boardStorageKey = (boardId: string) =>
  `wembii.crm.minimizedView.board.${boardId}`;

const parseConfig = (raw: string | null): MinimizedViewConfig | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<MinimizedViewConfig>;
    return {
      ...DEFAULT_MINIMIZED_VIEW,
      ...parsed,
      title: true,
    };
  } catch {
    return null;
  }
};

export const loadBoardMinimizedViewConfig = (
  boardId: string
): MinimizedViewConfig => {
  return (
    parseConfig(
      typeof localStorage !== "undefined"
        ? localStorage.getItem(boardStorageKey(boardId))
        : null
    ) || { ...DEFAULT_MINIMIZED_VIEW }
  );
};

export const saveBoardMinimizedViewConfig = (
  boardId: string,
  config: MinimizedViewConfig
) => {
  try {
    localStorage.setItem(
      boardStorageKey(boardId),
      JSON.stringify({ ...config, title: true })
    );
  } catch {
    // ignore quota / private mode
  }
};

export const loadMinimizedViewConfig = (
  boardId: string,
  listId: string
): MinimizedViewConfig => {
  try {
    const columnConfig = parseConfig(
      localStorage.getItem(columnStorageKey(boardId, listId))
    );
    if (columnConfig) return columnConfig;
    return loadBoardMinimizedViewConfig(boardId);
  } catch {
    return { ...DEFAULT_MINIMIZED_VIEW };
  }
};

export const saveMinimizedViewConfig = (
  boardId: string,
  listId: string,
  config: MinimizedViewConfig
) => {
  try {
    localStorage.setItem(
      columnStorageKey(boardId, listId),
      JSON.stringify({ ...config, title: true })
    );
  } catch {
    // ignore quota / private mode
  }
};

/** Apply the same minimized-view config to the board and every known column. */
export const saveMinimizedViewConfigForBoard = (
  boardId: string,
  listIds: string[],
  config: MinimizedViewConfig
) => {
  saveBoardMinimizedViewConfig(boardId, config);
  listIds.forEach((listId) => saveMinimizedViewConfig(boardId, listId, config));
};
